import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Sparkles, RefreshCw, Clock, GraduationCap, Briefcase } from "lucide-react";

export default function WeeklyBrief() {
  const [loading, setLoading] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/inbox");
      const list = (r.data || []).filter((m) => m.category === "weekly_brief");
      setBriefs(list);
      setLastRun(list[0]?.received_at || null);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runNow = async () => {
    setLoading(true);
    try {
      const r = await api.post("/me/weekly-brief/run");
      toast.success(`Brief ready — ${r.data.jobs_count} roles + skill gap`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not build brief");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="weekly-brief-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
          / Weekly Brief
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Every Sunday. One email.
          <br />
          <span className="text-gradient">Jobs + skill gaps.</span>
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl">
          Maqam scans the Kingdom's fresh postings against your target roles, finds the courses
          that close your skill gap, and delivers one consolidated brief to your platform inbox —
          no noise, no spam.
        </p>
      </div>

      <div className="border border-black/10 bg-white p-6 flex flex-wrap items-center gap-4" data-testid="run-card">
        <Bell className="w-6 h-6 text-[#0033FF]" />
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold">Trigger a brief right now</div>
          <div className="text-xs text-gray-600 mt-1">
            {lastRun
              ? `Last brief generated ${new Date(lastRun).toLocaleString()}`
              : "Briefs auto-run weekly. You can also pull one manually any time."}
          </div>
        </div>
        <button
          onClick={runNow}
          disabled={loading}
          className="bg-black text-white hover:bg-[#0033FF] font-bold px-5 py-3 flex items-center gap-2 transition-colors disabled:opacity-60"
          data-testid="brief-run-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Building…" : "Refresh my alerts"}
        </button>
      </div>

      <div className="space-y-4" data-testid="brief-history">
        {briefs.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Sparkles className="w-8 h-8 mx-auto mb-3 stroke-[1]" />
            <p className="text-sm">
              No briefs yet. Add at least one target job title on your profile and click{" "}
              <span className="font-bold text-black">Refresh my alerts</span>.
            </p>
          </div>
        )}
        {briefs.map((b, i) => (
          <div
            key={b.id}
            className="border border-black/10 bg-white p-6"
            data-testid={`brief-${i}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {new Date(b.received_at).toLocaleString()}
              </div>
            </div>
            <div className="font-display font-bold text-lg mb-3">{b.subject}</div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
              {b.body}
            </pre>
            {b.course_title && (
              <div className="mt-4 flex items-center gap-2 text-xs text-[#0033FF] font-bold">
                <GraduationCap className="w-3.5 h-3.5" /> Course spotlighted: {b.course_title}
              </div>
            )}
            {b.jobs_count != null && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                <Briefcase className="w-3.5 h-3.5" /> {b.jobs_count} fresh role{b.jobs_count === 1 ? "" : "s"}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
